// Admin Dispute Letter Queue (Sprint 3 / 7C).
// Drafts → review/edit → approve → send via Lob. Tracking number stored.
import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import AdminLayout from "../components/AdminLayout";
import { apiCall } from "../auth/AuthContext";
import { CheckCircle2, Loader2, Mail, Send, Edit3, X } from "lucide-react";

const STATUS_FILTERS = ["all", "draft", "approved", "sent", "error"];

export default function AdminDisputeQueue() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async (statusFilter) => {
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const d = await apiCall(`/api/admin/disputes/queue${q}`);
      return d.records || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const rows = await load(filter);
      if (cancelled) return;
      setRecords(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load, filter]);

  const reload = useCallback(async () => {
    const rows = await load(filter);
    setRecords(rows);
  }, [load, filter]);

  return (
    <AdminLayout title="DISPUTE LETTERS" kicker="Sprint 3 · 7C · AI Letter Engine + Lob mail">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="dispute-filter-bar">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`dispute-filter-${s}`}
            className={`px-4 py-1.5 text-xs font-mono uppercase tracking-[0.18em] border ${filter === s ? "border-jc-gold bg-jc-gold/10 text-jc-gold" : "border-jc-gold/20 text-jc-muted"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-jc-muted gap-3"><Loader2 className="animate-spin" size={18}/> Loading…</div>
      ) : records.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-jc-gold/20 rounded-xl">
          <Mail size={36} className="mx-auto text-jc-gold/50 mb-3"/>
          <p className="text-jc-muted">No dispute letters {filter !== "all" ? `with status "${filter}"` : "yet"}. Generate them from the AI Credit Analyzer.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-2" data-testid="dispute-list">
            {records.map((r) => (
              <button
                key={r.id}
                onClick={() => setActive(r)}
                data-testid={`dispute-row-${r.id}`}
                className={`w-full text-left border ${active?.id === r.id ? "border-jc-gold bg-jc-gold/5" : "border-jc-gold/15 hover:border-jc-gold/40"} px-5 py-4`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-jc-white">{r.recipient_name}</p>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-0.5">{r.client_email}</p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.18em] text-jc-muted">
                  <span>Round {r.round_number}</span>
                  <span>·</span>
                  <span>{r.recipient_type}</span>
                  {r.lob_tracking_number && <><span>·</span><span className="text-jc-gold">{r.lob_tracking_number}</span></>}
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            {active ? <LetterDetail key={active.id} letter={active} onChange={reload} /> :
              <div className="py-20 text-center border border-dashed border-jc-gold/15 rounded-xl text-jc-muted">Select a letter to review.</div>}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:    "bg-amber-500/15 text-amber-200 border-amber-500/30",
    approved: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    sent:     "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
    error:    "bg-red-500/15 text-red-200 border-red-500/30",
  };
  return <span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.22em] border ${map[status] || "bg-jc-muted/15 text-jc-muted border-jc-muted/30"}`}>{status}</span>;
}

function LetterDetail({ letter, onChange }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(letter.subject || "");
  const [html, setHtml] = useState(letter.html || "");
  const [addr, setAddr] = useState(letter.recipient_address || {});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const canSend = letter.status === "approved" &&
    addr.address_line1 && addr.address_city && addr.address_state && addr.address_zip;

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await apiCall(`/api/admin/disputes/${letter.id}`, {
        method: "PATCH", body: JSON.stringify({ subject, html }),
      });
      setEditing(false);
      await onChange();
      setMsg("✔ Saved");
    } catch (e) { setMsg(`× ${e.message || "Save failed"}`); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true); setMsg("");
    try {
      await apiCall(`/api/admin/disputes/${letter.id}/approve`, { method: "POST" });
      await onChange(); setMsg("✔ Approved");
    } catch (e) { setMsg(`× ${e.message}`); }
    finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true); setMsg("");
    try {
      await apiCall(`/api/admin/disputes/${letter.id}/send`, { method: "POST" });
      await onChange(); setMsg("✔ Mailed via Lob");
    } catch (e) { setMsg(`× ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-jc-gold/20 bg-jc-navy/40 p-6 rounded-xl" data-testid="dispute-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl text-jc-white">{letter.recipient_name}</h3>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-1">Round {letter.round_number} · {letter.recipient_type} · {letter.client_email}</p>
        </div>
        <StatusPill status={letter.status} />
      </div>

      <div className="mt-4 text-sm text-jc-white">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-jc-gold mb-1">Recipient address</p>
        <p>{letter.recipient_name}<br/>
          {addr.address_line1 || <em className="text-amber-300">— missing —</em>}<br/>
          {addr.address_city}, {addr.address_state} {addr.address_zip}</p>
      </div>

      {!addr.address_line1 && letter.status === "draft" && (
        <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-4 rounded">
          <p className="text-xs text-amber-200 mb-3">Furnisher address is unknown. Fill it in to enable sending:</p>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="addr-line1" placeholder="Street address" className="bg-jc-black border border-jc-gold/30 text-jc-white px-2 py-1.5 text-sm col-span-2"
              value={addr.address_line1 || ""} onChange={(e) => setAddr({ ...addr, address_line1: e.target.value })}/>
            <input data-testid="addr-city" placeholder="City" className="bg-jc-black border border-jc-gold/30 text-jc-white px-2 py-1.5 text-sm"
              value={addr.address_city || ""} onChange={(e) => setAddr({ ...addr, address_city: e.target.value })}/>
            <input data-testid="addr-state" placeholder="State" maxLength={2} className="bg-jc-black border border-jc-gold/30 text-jc-white px-2 py-1.5 text-sm"
              value={addr.address_state || ""} onChange={(e) => setAddr({ ...addr, address_state: e.target.value.toUpperCase() })}/>
            <input data-testid="addr-zip" placeholder="ZIP" className="bg-jc-black border border-jc-gold/30 text-jc-white px-2 py-1.5 text-sm col-span-2"
              value={addr.address_zip || ""} onChange={(e) => setAddr({ ...addr, address_zip: e.target.value })}/>
          </div>
          <button
            data-testid="addr-save"
            disabled={busy} onClick={async () => {
              setBusy(true); setMsg("");
              try {
                await apiCall(`/api/admin/disputes/${letter.id}`, {
                  method: "PATCH",
                  body: JSON.stringify({ recipient_address: addr }),
                });
                await onChange();
                setMsg("✔ Address saved");
              } catch (e) { setMsg(`× ${e.message || "Save failed"}`); }
              finally { setBusy(false); }
            }}
            className="mt-3 jc-btn jc-btn-outline !text-xs !py-2"
          >Save address</button>
        </div>
      )}

      <div className="mt-5">
        {!editing ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-jc-gold">◆ Letter preview</p>
              {letter.status === "draft" && (
                <button data-testid="letter-edit" onClick={() => setEditing(true)} className="jc-btn jc-btn-outline !text-xs !py-1.5 !px-3"><Edit3 size={12}/> Edit</button>
              )}
            </div>
            <p className="text-sm text-jc-white font-semibold mb-2">{letter.subject}</p>
            <div className="border border-jc-gold/15 bg-white text-jc-black p-5 rounded max-h-96 overflow-y-auto text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(letter.html || "") }} data-testid="letter-preview" />
          </>
        ) : (
          <div>
            <input data-testid="letter-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-jc-black border border-jc-gold/30 text-jc-white px-3 py-2 text-sm mb-2" placeholder="Subject"/>
            <textarea data-testid="letter-html-input" value={html} onChange={(e) => setHtml(e.target.value)} rows={18} className="w-full bg-jc-black border border-jc-gold/30 text-jc-white px-3 py-2 text-sm font-mono"/>
            <div className="flex gap-2 mt-3">
              <button data-testid="letter-save" disabled={busy} onClick={save} className="jc-btn jc-btn-primary !text-xs !py-2">Save</button>
              <button onClick={() => { setEditing(false); setSubject(letter.subject); setHtml(letter.html); }} className="jc-btn jc-btn-outline !text-xs !py-2"><X size={12}/> Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2 pt-5 border-t border-jc-gold/15">
        {letter.status === "draft" && (
          <button data-testid="letter-approve" disabled={busy} onClick={approve} className="jc-btn jc-btn-primary !text-xs"><CheckCircle2 size={12}/> Approve</button>
        )}
        {letter.status === "approved" && (
          <button data-testid="letter-send" disabled={busy || !canSend} onClick={send} className="jc-btn jc-btn-primary !text-xs disabled:opacity-50"><Send size={12}/> Send via Lob</button>
        )}
        {letter.status === "sent" && letter.lob_url && (
          <a href={letter.lob_url} target="_blank" rel="noreferrer" className="jc-btn jc-btn-outline !text-xs">View Lob letter</a>
        )}
      </div>
      {msg && <p className={`mt-3 text-sm ${msg.startsWith("✔") ? "text-emerald-300" : "text-red-300"}`} data-testid="dispute-msg">{msg}</p>}
      {letter.status === "sent" && letter.lob_tracking_number && (
        <p className="mt-3 text-xs font-mono uppercase tracking-[0.18em] text-jc-muted">◆ Tracking: <span className="text-jc-gold">{letter.lob_tracking_number}</span></p>
      )}
    </div>
  );
}
